/**
 * Admin · Weekly Pulse Emails
 * ===========================
 *
 * Operator surface for the nurture funnel. Four panels:
 *   1. Status — queue size, total leads, unsubscribed, last runs
 *   2. Preview — render the current week's email for any ZIP, inline iframe
 *   3. Send test — fire the live template to any address as a smoke test
 *   4. Run now — manual trigger for the cron loop (dry-run by default)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const post = (path, body) =>
  axios.post(`${API_URL}${path}`, body || {}, { withCredentials: true });

const get = (path) =>
  axios.get(`${API_URL}${path}`, { withCredentials: true });

function StatusPanel({ status, onReload, loading }) {
  const cards = [
    { label: 'Queued this cycle', value: status?.queued_now, accent: 'text-emerald-700' },
    { label: 'Total leads', value: status?.total_leads, accent: 'text-slate-700' },
    { label: 'Unsubscribed', value: status?.unsubscribed, accent: 'text-rose-700' },
    {
      label: 'Resend configured',
      value: status?.resend_configured ? 'yes' : 'NO',
      accent: status?.resend_configured ? 'text-emerald-700' : 'text-rose-700',
    },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="nurture-status-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Nurture status</div>
          <div className="text-slate-900 text-lg font-semibold">
            {status
              ? `${status.cron_interval_hours}-hour cron · ${status.cooldown_days}-day cooldown per lead · max ${status.max_per_run} per run`
              : 'loading…'}
          </div>
        </div>
        <button
          onClick={onReload}
          disabled={loading}
          className="text-xs uppercase tracking-widest text-slate-600 hover:text-emerald-700 border border-slate-300 rounded-md px-3 py-1"
          data-testid="nurture-reload-btn"
        >
          {loading ? 'refreshing…' : 'refresh'}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div
            key={c.label}
            className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3"
            data-testid={`nurture-stat-${c.label.toLowerCase().replace(/ /g, '-')}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              {c.label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${c.accent}`}>
              {c.value ?? '—'}
            </div>
          </div>
        ))}
      </div>

      {(status?.recent_runs || []).length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            Last 5 runs
          </div>
          <ul className="text-sm space-y-1" data-testid="nurture-recent-runs">
            {status.recent_runs.map((r, i) => (
              <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                <span className="text-slate-600">
                  {new Date(r.run_at).toLocaleString()}
                  {r.dry_run ? ' · dry' : ''}
                </span>
                <span className="text-slate-900 tabular-nums">
                  {r.sent} sent · {r.failed} failed · {r.skipped} skipped
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PreviewPanel() {
  const [zip, setZip] = useState('78664');
  const [url, setUrl] = useState(null);

  const open = () => {
    const clean = (zip || '').trim();
    if (!clean) return;
    // iframe srcdoc forces the auth cookie to travel; use plain URL instead
    setUrl(`${API_URL}/api/admin/nurture/preview?zip=${encodeURIComponent(clean)}&_t=${Date.now()}`);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="nurture-preview-panel">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
        Email preview
      </div>
      <div className="flex gap-2 items-center mb-3">
        <input
          type="text"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          placeholder="ZIP"
          className="border border-slate-300 rounded-md px-3 py-2 text-sm w-32"
          data-testid="nurture-preview-zip-input"
        />
        <button
          onClick={open}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md px-4 py-2 text-sm font-semibold"
          data-testid="nurture-preview-render-btn"
        >
          Render this ZIP
        </button>
      </div>
      {url ? (
        <iframe
          src={url}
          title="Nurture email preview"
          className="w-full h-[700px] border border-slate-200 rounded-lg bg-slate-50"
          data-testid="nurture-preview-iframe"
        />
      ) : (
        <div className="text-sm text-slate-500 italic">Enter a ZIP and render to see the email as it'll ship this week.</div>
      )}
    </div>
  );
}

function SendTestPanel() {
  const [email, setEmail] = useState('');
  const [zip, setZip] = useState('78664');
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!email.includes('@')) return;
    setSending(true);
    setResult(null);
    try {
      const r = await post(
        `/api/admin/nurture/send-test?email=${encodeURIComponent(email)}&zip=${encodeURIComponent(zip)}`,
      );
      setResult(r.data);
    } catch (e) {
      setResult({ status: 'error', error: e?.response?.data?.detail || String(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="nurture-sendtest-panel">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
        Send live test to one address
      </div>
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          className="border border-slate-300 rounded-md px-3 py-2 text-sm flex-1 min-w-[220px]"
          data-testid="nurture-sendtest-email-input"
        />
        <input
          type="text"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          placeholder="ZIP"
          className="border border-slate-300 rounded-md px-3 py-2 text-sm w-28"
          data-testid="nurture-sendtest-zip-input"
        />
        <button
          onClick={send}
          disabled={sending || !email.includes('@')}
          className="bg-emerald-700 disabled:bg-slate-300 hover:bg-emerald-800 text-white rounded-md px-4 py-2 text-sm font-semibold"
          data-testid="nurture-sendtest-btn"
        >
          {sending ? 'sending…' : 'Send test'}
        </button>
      </div>
      {result ? (
        <pre className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs overflow-x-auto" data-testid="nurture-sendtest-result">
          {JSON.stringify(result, null, 2)}
        </pre>
      ) : (
        <div className="text-xs text-slate-500 italic">
          Sends the live template — uses one Resend credit. Check inbox + spam.
        </div>
      )}
    </div>
  );
}

function RunNowPanel({ onAfter }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    setBusy(true);
    setResult(null);
    try {
      const r = await post('/api/admin/nurture/run-now', { dry_run: dryRun });
      setResult(r.data);
      onAfter?.();
    } catch (e) {
      setResult({ status: 'error', error: e?.response?.data?.detail || String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="nurture-runnow-panel">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
        Manual cron trigger
      </div>
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => run(true)}
          disabled={busy}
          className="border border-slate-300 hover:border-slate-500 text-slate-700 rounded-md px-4 py-2 text-sm font-semibold"
          data-testid="nurture-runnow-dry-btn"
        >
          Dry run
        </button>
        <button
          onClick={() => {
            if (window.confirm('Actually send emails to every eligible lead now? This uses Resend credits.')) {
              run(false);
            }
          }}
          disabled={busy}
          className="bg-rose-700 hover:bg-rose-800 text-white rounded-md px-4 py-2 text-sm font-semibold"
          data-testid="nurture-runnow-live-btn"
        >
          Send live now
        </button>
      </div>
      {result ? (
        <pre className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs overflow-x-auto max-h-64" data-testid="nurture-runnow-result">
          {JSON.stringify(result, null, 2)}
        </pre>
      ) : (
        <div className="text-xs text-slate-500 italic">
          Dry-run builds subjects + snapshots without sending. Live sends through Resend.
        </div>
      )}
    </div>
  );
}

export default function AdminNurturePage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get('/api/admin/nurture/status');
      setStatus(r.data);
    } catch {
      /* ignore — auth redirect or transient */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-100 px-6 py-8" data-testid="admin-nurture-page">
      <div className="max-w-5xl mx-auto space-y-5">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">TurfPulse · Admin</div>
          <h1 className="text-3xl font-semibold text-slate-900">Weekly Pulse Emails</h1>
          <p className="text-slate-600 text-sm mt-1 max-w-2xl">
            Nurture funnel for stagnant <code className="bg-slate-200 px-1 rounded">/hey</code> leads. One email per ZIP
            per 7 days. Background loop runs every {status?.cron_interval_hours ?? 6}h; use "Send live now" for ad-hoc blasts.
          </p>
        </div>

        <StatusPanel status={status} onReload={load} loading={loading} />
        <PreviewPanel />
        <SendTestPanel />
        <RunNowPanel onAfter={load} />
      </div>
    </div>
  );
}
